import { PreservingNavigate } from "@components/common/preserve-navigation/preserving-navigate";
import { useMediaQueryList } from "@hooks/use-media-query";
import { AccountSettingsLayout } from "@pages/account-settings/account-settings-layout/account-settings-layout";
import { canGuestOpenAccountSettingsTab } from "@pages/account-settings/account-settings-utils";
import { UserProvider } from "@pages/account-settings/user-provider";
import { AccountSettingsTabs } from "@router/route-params";
import {
  generateAccountSettingsRoute,
  useAppParams,
} from "@router/router-helper";
import { useAppSelector } from "@store/store-helper";
import {
  fetchingUserFlagsSelector,
  loggedInUserSelector,
} from "@store/user/user-selector";

/** Content for the account settings page */
export function AccountSettingsPage(): JSX.Element | undefined {
  const { accountSettingsTab } = useAppParams();
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const { loggedInUserFetchingStatus } = useAppSelector(
    fetchingUserFlagsSelector
  );

  // currentUser cannot be used here. This is because to get the current user, we need to attach a company ID.
  // However, if user directly opens this page, no company information is available and to have one,
  // an additional api request should be done to get the company ids and another one to get the currentUser information.
  // To prevent this, we are using loggedInUser instead of currentUser.
  // Note: The logged in user is fetched centrally in UserLoader component.
  const loggedInUser = useAppSelector(loggedInUserSelector);

  // Wait until the user-fetch lifecycle is initialized and finished.
  // Redux initial state (fetchingStatus="uninitialized", loggedInUser=null) looks like a
  // guest on first render; only show content after fetch is done.
  if (
    loggedInUserFetchingStatus === "uninitialized" ||
    loggedInUserFetchingStatus === "pending"
  ) {
    return undefined;
  }

  const isGuestUser = !loggedInUser;

  // If the user is a guest user, redirect to preferences tab
  // Checking accountSettingsTab is necessary to avoid infinite loop
  if (isGuestUser && !canGuestOpenAccountSettingsTab(accountSettingsTab)) {
    return (
      <PreservingNavigate
        to={generateAccountSettingsRoute(AccountSettingsTabs.preferences)}
      />
    );
  }

  // Redirect to security tab if no tab is selected and the user is logged in
  // In mobile view, we show the tab selection page instead
  if (
    loggedInUser &&
    accountSettingsTab === undefined &&
    !isScreenSmOrSmaller
  ) {
    return (
      <PreservingNavigate
        to={generateAccountSettingsRoute(AccountSettingsTabs.security)}
      />
    );
  }

  return (
    <UserProvider user={loggedInUser ?? undefined}>
      <AccountSettingsLayout />
    </UserProvider>
  );
}
